/**
 * ProductionDetail — single screen showing where a production is in the
 * research/writing pipeline + a single Continue button that fires the next
 * stage. Designed for Flip 7: one big tap target, polling so the user can
 * see state advance without manual refresh.
 *
 * Stages mapped to the Continue button:
 *   created                 → "Find sources" (auto-suggest)
 *   sources_uploaded        → "Draft factsheet"
 *   factsheet_drafting      → spinner ("Drafting factsheet…")
 *   factsheet_drafted       → "Approve + generate outline"
 *   factsheet_approved      → spinner (auto-cascade fires outline)
 *   outline_generating      → spinner ("Building outline…")
 *   outline_generated       → "Approve + generate scripts"
 *   outline_approved        → spinner (auto-cascade fires scripts)
 *   script_generating       → spinner ("Writing scripts…")
 *   script_generated        → "Done — open on desktop to review"
 *   later stages            → "Open on desktop" (audio/render not on mob)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowLeft,
  Check,
  CircleDashed,
  ExternalLink,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { api } from '../lib/api';
import { BottomNav } from '../components/BottomNav';

interface ProductionDetailResponse {
  production: {
    id: string;
    topic: string;
    state: string;
    channelId: string;
    workingTitle?: string;
    subtitle?: string | null;
    podcastEpisodeNumber?: number | null;
    publicDescription?: string | null;
    thumbnailUrl?: string | null;
    finalVideoUrl?: string | null;
    youtubeVideoId?: string | null;
    thumbnailVariantsJson?: ThumbnailVariant[] | null;
    thumbnailSelectedVariant?: number | null;
  };
  channel?: { id: string; name: string };
  sources?: Array<{ id: string; citationKey: string; title: string }>;
  factsheet?: { id: string };
  outline?: { id: string };
  chapters?: Array<{
    id: string;
    orderIndex: number;
    title: string;
    state: string;
    audioR2Key?: string | null;
    imageR2Key?: string | null;
  }>;
}

interface ThumbnailVariant {
  variantIndex: number;
  styleLabel: string;
  finalR2Key: string;
  composeJobId: string | null;
  bgError: string | null;
  pHash: string | null;
  maxSimilarity: number | null;
  similarToWorkingTitle: string | null;
  similarToHammingDistance: number | null;
  previewUrl: string | null;
}

interface YouTubeDailyRow {
  id: string;
  productionId: string | null;
  youtubeVideoId: string;
  date: string;
  polledAt: number;
  views: number | null;
  watchTimeSeconds: number | null;
  averageViewDurationSeconds: number | null;
  subscribersGained: number | null;
  subscribersLost: number | null;
  workingTitle: string | null;
}

interface YouTubeDailyPayload {
  days: number;
  rows: YouTubeDailyRow[];
}

interface YouTubeDailyPoint {
  date: string;
  label: string;
  views: number;
  watchHours: number;
}

type Phase = 'sources' | 'factsheet' | 'outline' | 'scripts' | 'audio';

const PHASE_LABEL: Record<Phase, string> = {
  sources: 'Sources',
  factsheet: 'Factsheet',
  outline: 'Outline',
  scripts: 'Scripts',
  audio: 'Audio',
};

export function ProductionDetail() {
  const { id = '' } = useParams();
  const [data, setData] = useState<ProductionDetailResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [ytDaily, setYtDaily] = useState<YouTubeDailyPayload | null>(null);
  const pollingRef = useRef<number | null>(null);

  async function refresh() {
    try {
      const r = await api.get<ProductionDetailResponse>(`/productions/${id}`);
      setData(r);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    void refresh();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const productionId = data?.production.id;
    const youtubeVideoId = data?.production.youtubeVideoId;
    if (!productionId || !youtubeVideoId) {
      setYtDaily(null);
      return;
    }
    let cancelled = false;
    api.get<YouTubeDailyPayload>(`/analytics/youtube/daily?days=28&productionId=${productionId}`)
      .then((rows) => {
        if (!cancelled) setYtDaily(rows);
      })
      .catch(() => {
        if (!cancelled) setYtDaily(null);
      });
    return () => {
      cancelled = true;
    };
  }, [data?.production.id, data?.production.youtubeVideoId]);

  // Poll every 4s while in a transient (-ing) state. Stop when stable so
  // we're not hammering the API on a Flip 7's flaky LTE.
  useEffect(() => {
    const state = data?.production.state;
    if (!state) return;
    const isTransient =
      state.endsWith('ing') ||
      state.endsWith('ed') === false ||
      state === 'factsheet_drafting' ||
      state === 'outline_generating' ||
      state === 'script_generating';
    if (pollingRef.current != null) {
      window.clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (isTransient) {
      pollingRef.current = window.setInterval(() => {
        void refresh();
      }, 4000);
    }
    return () => {
      if (pollingRef.current != null) {
        window.clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [data?.production.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const ytDailyChart = useMemo<YouTubeDailyPoint[]>(() => {
    return (ytDaily?.rows ?? [])
      .map((row) => ({
        date: row.date,
        label: formatShortDate(row.date),
        views: row.views ?? 0,
        watchHours: (row.watchTimeSeconds ?? 0) / 3600,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [ytDaily]);

  const ytDailyTotals = useMemo(() => ({
    views: ytDailyChart.reduce((sum, row) => sum + row.views, 0),
    watchHours: ytDailyChart.reduce((sum, row) => sum + row.watchHours, 0),
    polledAt: ytDaily?.rows.reduce<number | null>(
      (latest, row) => (latest === null || row.polledAt > latest ? row.polledAt : latest),
      null,
    ) ?? null,
  }), [ytDaily, ytDailyChart]);

  if (err)
    return (
      <div className="min-h-screen p-4 pb-24">
        <div className="card border-danger/50 text-danger text-sm">{err}</div>
        <BottomNav active="productions" />
      </div>
    );
  if (!data) return <LoadingScreen />;

  const state = data.production.state;
  const currentPhase = phaseFor(state);

  async function fire(action: string, label: string) {
    setBusy(true);
    setActionMsg(label);
    setErr(null);
    try {
      await api.post(`/productions/${id}${action}`);
      // Re-fetch immediately so the spinner can take over for transient states.
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setActionMsg(null);
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-10 bg-bg/95 backdrop-blur border-b border-border px-4 py-3 flex items-center gap-2">
        <Link to="/productions" className="tap p-2 -ml-2" aria-label="Back">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="font-serif text-lg leading-tight line-clamp-1">
            {data.production.topic}
          </h1>
          <p className="text-xs text-muted">{data.channel?.name ?? data.production.channelId}</p>
        </div>
        <button onClick={refresh} className="tap p-2" aria-label="Refresh">
          <RefreshCw className="h-5 w-5" />
        </button>
      </header>

      <main className="flex-1 px-4 py-4 space-y-4 pb-32">
        {/* Phase strip */}
        <div className="card">
          <div className="grid grid-cols-5 gap-1 text-center text-[10px]">
            {(['sources', 'factsheet', 'outline', 'scripts', 'audio'] as Phase[]).map((p) => (
              <PhaseStep
                key={p}
                label={PHASE_LABEL[p]}
                state={phaseState(currentPhase, p)}
              />
            ))}
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-2">
          <Stat n={data.sources?.length ?? 0} label="sources" />
          <Stat n={data.chapters?.length ?? 0} label="chapters" />
          <Stat
            n={data.chapters?.filter((c) => c.state === 'generated').length ?? 0}
            label="scripts done"
          />
        </div>

        {/* State + action area */}
        <div className="card space-y-3">
          <div className="text-xs text-muted uppercase tracking-wider">Current state</div>
          <div className="font-mono text-sm text-accent">{state.replace(/_/g, ' ')}</div>
          {actionMsg && (
            <div className="flex items-center gap-2 text-sm text-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{actionMsg}</span>
            </div>
          )}
        </div>

        <NextAction
          state={state}
          busy={busy}
          fire={fire}
          productionId={id}
        />

        {/* Source search + import — THE key mobile gap. A production sits
            in 'created' until at least one source is imported (the import
            route auto-advances created → sources_uploaded). Without this
            panel there was no way to add sources on mobile, so episodes
            couldn't be started from the phone at all. */}
        {(state === 'created' || state === 'sources_uploaded') && (
          <MobileSourceSearch
            productionId={id}
            defaultQuery={data.production.topic}
            onImported={refresh}
          />
        )}

        {/* Script generation progress — visible during fan-out across chapters */}
        {state === 'script_generating' && data.chapters && data.chapters.length > 0 && (
          <ScriptProgress chapters={data.chapters} />
        )}

        {/* TTS / master / assemble progress — visible while audio stages run */}
        {(state === 'tts_synthesising' ||
          state === 'tts_synthesised' ||
          state === 'audio_mastering' ||
          state === 'audio_mastered' ||
          state === 'assembling') && (
          <TtsProgress chapters={data.chapters ?? []} state={state} />
        )}

        {/* Thumbnail variants — generate + pick, visible once we have a final video */}
        {(state === 'assembled' ||
          state === 'ready_to_upload' ||
          state === 'uploaded') && (
          <ThumbnailVariants
            production={data.production}
            onChanged={refresh}
          />
        )}

        {/* YouTube upload form — visible when production is ready_to_upload */}
        {state === 'ready_to_upload' && (
          <YouTubeUploadPanel production={data.production} onChanged={refresh} />
        )}

        {/* YouTube update push — visible when already uploaded */}
        {state === 'uploaded' && data.production.youtubeVideoId && (
          <YouTubeUpdatePanel production={data.production} onChanged={refresh} />
        )}

        {state === 'uploaded' && data.production.youtubeVideoId && (
          <YouTubeMobileStatsCard
            videoId={data.production.youtubeVideoId}
            points={ytDailyChart}
            totals={ytDailyTotals}
          />
        )}

        {/* Read-only previews */}
        {data.sources && data.sources.length > 0 && (
          <details className="card">
            <summary className="text-sm font-medium cursor-pointer">
              {data.sources.length} sources
            </summary>
            <ul className="mt-3 space-y-1 text-xs text-muted">
              {data.sources.map((s) => (
                <li key={s.id} className="line-clamp-1">
                  · {s.title}
                </li>
              ))}
            </ul>
          </details>
        )}
        {data.chapters && data.chapters.length > 0 && (
          <details className="card">
            <summary className="text-sm font-medium cursor-pointer">
              {data.chapters.length} chapters
            </summary>
            <ul className="mt-3 space-y-1 text-xs">
              {data.chapters
                .sort((a, b) => a.orderIndex - b.orderIndex)
                .map((c) => (
                  <li key={c.id} className="flex items-center gap-2">
                    <span className="text-muted w-5">{c.orderIndex}.</span>
                    <span className="flex-1 line-clamp-1">{c.title}</span>
                    {c.state === 'generated' ? (
                      <Check className="h-3 w-3 text-emerald-300" />
                    ) : c.state === 'generating' ? (
                      <Loader2 className="h-3 w-3 animate-spin text-accent" />
                    ) : (
                      <CircleDashed className="h-3 w-3 text-muted" />
                    )}
                  </li>
                ))}
            </ul>
          </details>
        )}
      </main>

      <BottomNav active="productions" />
    </div>
  );
}

function PhaseStep({
  label,
  state,
}: {
  label: string;
  state: 'done' | 'active' | 'pending';
}) {
  const cls =
    state === 'done'
      ? 'text-emerald-300'
      : state === 'active'
        ? 'text-accent font-semibold'
        : 'text-muted/50';
  return (
    <div className={cls}>
      <div className="flex items-center justify-center gap-1">
        {state === 'done' ? (
          <Check className="h-3 w-3" />
        ) : state === 'active' ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <CircleDashed className="h-3 w-3" />
        )}
      </div>
      <div className="mt-0.5">{label}</div>
    </div>
  );
}

function Stat({ n, label }: { n: number; label: string }) {
  return (
    <div className="card text-center">
      <div className="text-2xl tabular-nums">{n}</div>
      <div className="text-[10px] text-muted uppercase tracking-wider">{label}</div>
    </div>
  );
}

function YouTubeMobileStatsCard({
  videoId,
  points,
  totals,
}: {
  videoId: string;
  points: YouTubeDailyPoint[];
  totals: { views: number; watchHours: number; polledAt: number | null };
}) {
  return (
    <div className="card space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs text-muted uppercase tracking-wider">YouTube 28d</div>
          <div className="mt-1 grid grid-cols-2 gap-4">
            <div>
              <div className="text-xl tabular-nums">{totals.views.toLocaleString()}</div>
              <div className="text-[10px] text-muted uppercase tracking-wider">views</div>
            </div>
            <div>
              <div className="text-xl tabular-nums">{totals.watchHours.toFixed(1)}h</div>
              <div className="text-[10px] text-muted uppercase tracking-wider">watch</div>
            </div>
          </div>
        </div>
        <a
          href={`https://www.youtube.com/watch?v=${videoId}`}
          target="_blank"
          rel="noreferrer"
          className="tap p-2 text-muted"
          aria-label="Open on YouTube"
        >
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>

      {points.length === 0 ? (
        <div className="rounded-md border border-border bg-border/20 p-3 text-xs text-muted">
          No daily Analytics rows yet.
        </div>
      ) : (
        <div className="h-36">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 8, right: 4, left: -28, bottom: 0 }}>
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
              />
              <YAxis hide domain={[0, 'auto']} />
              <Tooltip
                contentStyle={{
                  background: '#111116',
                  border: '1px solid #2b2b33',
                  borderRadius: 8,
                  color: '#f4f4f5',
                  fontSize: 12,
                }}
                labelStyle={{ color: '#d4d4d8' }}
                formatter={(value) => [Number(value).toLocaleString(), 'Views']}
              />
              <Line
                type="monotone"
                dataKey="views"
                stroke="#8b5cf6"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {totals.polledAt && (
        <div className="text-[10px] text-muted">Updated {formatRelative(totals.polledAt)}</div>
      )}
    </div>
  );
}

/**
 * The state-driven Continue button. One big tap target, no hidden menus.
 * For pre-script states we run the actual stage (suggests/draft/approve).
 * For script_generated and after we punt to desktop because audio/render
 * needs the bigger screen + L40 oversight.
 */
function NextAction({
  state,
  busy,
  fire,
  productionId,
}: {
  state: string;
  busy: boolean;
  fire: (action: string, label: string) => Promise<void>;
  productionId: string;
}) {
  const dashboardUrl = `https://nightshade.gameonsolutions.uk/productions/${productionId}`;

  // Working states — just show a spinner, no action
  if (
    state === 'factsheet_drafting' ||
    state === 'outline_generating' ||
    state === 'script_generating' ||
    state === 'script_factchecking' ||
    state === 'script_revising' ||
    state === 'preprocessing'
  ) {
    return (
      <div className="card text-center py-6 text-muted text-sm">
        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-accent" />
        Working… polling every 4 seconds
      </div>
    );
  }

  if (state === 'created') {
    // The "Add sources" search panel renders directly below this. No
    // button here — pointing the user at the search panel is clearer
    // than the old /suggest call (which only suggested + had no mobile
    // UI to act on the suggestions).
    return (
      <div className="card text-center py-4 text-sm text-muted">
        ↓ Search + import at least one source below to begin
      </div>
    );
  }

  if (state === 'sources_uploaded') {
    return (
      <BigButton onClick={() => fire('/factsheet/draft', 'Starting factsheet…')} disabled={busy}>
        Draft factsheet
      </BigButton>
    );
  }

  if (state === 'factsheet_drafted') {
    return (
      <BigButton
        onClick={() => fire('/factsheet/approve', 'Approving + queuing outline…')}
        disabled={busy}
      >
        Approve + generate outline
      </BigButton>
    );
  }

  if (state === 'outline_generated') {
    return (
      <BigButton
        onClick={() => fire('/outline/approve', 'Approving + queuing scripts…')}
        disabled={busy}
      >
        Approve + generate scripts
      </BigButton>
    );
  }

  if (state === 'script_generated') {
    // Auto-cascade should fire factcheck. Show a "Continue → factcheck" button
    // for cases where the cascade dropped; otherwise this state should be
    // brief. Tap = explicit re-trigger.
    return (
      <BigButton
        onClick={() => fire('/factcheck', 'Running factcheck…')}
        disabled={busy}
      >
        Run factcheck
      </BigButton>
    );
  }

  if (state === 'script_revised_pending_review') {
    return (
      <BigButton
        onClick={() => fire('/script/approve', 'Approving + queuing audio…')}
        disabled={busy}
      >
        Approve script + start audio
      </BigButton>
    );
  }

  // For TTS/master/assemble, the TtsProgress panel below shows N/14 chapters.
  // The NextAction here just shows "Audio rendering" so the screen has a
  // clear active-state pill.
  if (
    state === 'tts_synthesising' ||
    state === 'tts_synthesised' ||
    state === 'audio_mastering' ||
    state === 'audio_mastered' ||
    state === 'assembling'
  ) {
    return (
      <div className="card text-center py-6 text-muted text-sm">
        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-accent" />
        Audio + video rendering on Hetzner…
      </div>
    );
  }

  // assembled / ready_to_upload: thumbnail variant + upload panels below take
  // over. Show a short prompt to draw the eye there.
  if (state === 'assembled' || state === 'ready_to_upload') {
    return (
      <div className="card text-center py-4 text-sm text-emerald-300">
        ✓ Video assembled — pick a thumbnail variant + upload below
      </div>
    );
  }

  if (state === 'uploaded') {
    return (
      <div className="card text-center py-4 text-sm text-emerald-300">
        ✓ Live on YouTube — push metadata + thumbnail updates below
      </div>
    );
  }

  // Anything else (manual gates we haven't built UI for, error states, etc.)
  // → desktop fallback.
  return (
    <a
      href={dashboardUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="btn-primary py-4 text-center flex items-center justify-center gap-2"
    >
      Open on desktop
      <ExternalLink className="h-4 w-4" />
    </a>
  );
}

// ----------------------------------------------------------------------
// Script generation progress — N/14 chapters scripted via parallel Sonnet calls
// ----------------------------------------------------------------------

function ScriptProgress({
  chapters,
}: {
  chapters: NonNullable<ProductionDetailResponse['chapters']>;
}) {
  const total = chapters.length;
  const generated = chapters.filter((c) => c.state === 'generated').length;
  const generating = chapters.filter((c) => c.state === 'generating').length;
  const pct = total > 0 ? Math.round((generated / total) * 100) : 0;
  return (
    <div className="card space-y-3">
      <div className="text-xs text-muted uppercase tracking-wider">Script generation</div>
      <div className="text-sm">
        Sonnet is writing chapters in parallel. ~2 min per chapter, ~3 concurrent.
      </div>
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-border overflow-hidden">
          <div className="h-full bg-accent transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-xs tabular-nums text-muted w-14 text-right">
          {generated} / {total}
        </div>
      </div>
      {generating > 0 && (
        <div className="text-xs text-muted">{generating} writing now…</div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// TTS / audio progress — N/14 chapters synthesised + master/assemble pill
// ----------------------------------------------------------------------

function TtsProgress({
  chapters,
  state,
}: {
  chapters: NonNullable<ProductionDetailResponse['chapters']>;
  state: string;
}) {
  const total = chapters.length;
  const synthesised = chapters.filter(
    (c) => c.state === 'synthesised' || !!c.audioR2Key,
  ).length;
  const pct = total > 0 ? Math.round((synthesised / total) * 100) : 0;

  let phaseLabel = 'TTS — synthesising chapter audio';
  if (state === 'tts_synthesised') phaseLabel = 'TTS done — queueing master mix';
  if (state === 'audio_mastering') phaseLabel = 'Mixing master MP3 on Hetzner…';
  if (state === 'audio_mastered') phaseLabel = 'Master done — queueing final assemble';
  if (state === 'assembling') phaseLabel = 'Assembling final MP4 on Hetzner…';

  return (
    <div className="card space-y-3">
      <div className="text-xs text-muted uppercase tracking-wider">Audio render</div>
      <div className="text-sm">{phaseLabel}</div>
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-border overflow-hidden">
          <div
            className="h-full bg-accent transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="text-xs tabular-nums text-muted w-14 text-right">
          {synthesised} / {total}
        </div>
      </div>
      {state === 'tts_synthesising' && synthesised === 0 && (
        <div className="text-xs text-muted">
          First chapter usually 8–15 min on Replicate Chatterbox. Hetzner processes one at a time.
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// Thumbnail variants — generate 3, vertical stack, similarity badges, pick
// ----------------------------------------------------------------------

function ThumbnailVariants({
  production,
  onChanged,
}: {
  production: ProductionDetailResponse['production'];
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const variants = production.thumbnailVariantsJson ?? [];
  const selectedIdx = production.thumbnailSelectedVariant ?? null;
  const [mainTitle, setMainTitle] = useState(production.topic.toUpperCase());
  const [subtitle, setSubtitle] = useState(production.subtitle ?? '');
  const [episodeNumber, setEpisodeNumber] = useState(production.podcastEpisodeNumber ?? 1);

  async function regenerate() {
    if (!mainTitle.trim()) {
      setError('Title required');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/productions/${production.id}/thumbnail/generate`, {
        mainTitle: mainTitle.trim(),
        subtitle: subtitle.trim() || undefined,
        episodeNumber,
      });
      // Variants land in D1 over ~30s as compose jobs complete; refresh once
      // immediately + again at +15s so the panel updates in two waves.
      await onChanged();
      setTimeout(() => { void onChanged(); }, 15_000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generate failed');
    } finally {
      setBusy(false);
    }
  }

  async function selectVariant(idx: number) {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/productions/${production.id}/thumbnail/select`, { variantIndex: idx });
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Select failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-3">
      <div className="text-xs text-muted uppercase tracking-wider">Thumbnail variants</div>
      <p className="text-xs text-muted">
        Generates 3 distinct variants + scores each against the channel's recent thumbnails. Pick the most distinct one. ~£0.18 per regen.
      </p>

      <div className="space-y-2">
        <input
          value={mainTitle}
          onChange={(e) => setMainTitle(e.target.value)}
          placeholder="Title (e.g. GLAMIS CASTLE)"
          className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
        />
        <input
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          placeholder="Subtitle (italic, optional)"
          className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
        />
        <div className="flex gap-2 items-center">
          <span className="text-xs text-muted">Episode</span>
          <input
            type="number"
            min={1}
            max={999}
            value={episodeNumber}
            onChange={(e) => setEpisodeNumber(Number(e.target.value))}
            className="w-20 rounded-md border border-border bg-bg px-3 py-2 text-sm tabular-nums"
          />
          <button
            onClick={regenerate}
            disabled={busy}
            className="btn-primary flex-1 py-2 text-sm"
          >
            {busy ? '…' : 'Generate 3 variants'}
          </button>
        </div>
      </div>

      {error && <div className="text-sm text-danger">{error}</div>}

      {variants.length === 0 ? (
        <p className="text-xs text-muted">No variants yet — tap Generate above.</p>
      ) : (
        <div className="space-y-3">
          {variants.map((v) => (
            <VariantCard
              key={v.variantIndex}
              variant={v}
              isSelected={selectedIdx === v.variantIndex}
              disabled={busy}
              onSelect={() => selectVariant(v.variantIndex)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function VariantCard({
  variant,
  isSelected,
  disabled,
  onSelect,
}: {
  variant: ThumbnailVariant;
  isSelected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const sim = variant.maxSimilarity;
  let badgeClass = 'bg-border text-muted';
  let badgeText = 'pHash pending…';
  if (sim !== null && sim !== undefined) {
    const pct = Math.round(sim * 100);
    if (sim < 0.55) {
      badgeClass = 'bg-emerald-950/60 text-emerald-300';
      badgeText = `${pct}% similar — distinct`;
    } else if (sim < 0.75) {
      badgeClass = 'bg-amber-950/60 text-amber-300';
      badgeText = `${pct}% similar — moderate`;
    } else {
      badgeClass = 'bg-red-950/60 text-red-300';
      badgeText = `${pct}% similar — near-duplicate`;
    }
  }
  if (variant.bgError) {
    badgeClass = 'bg-red-950/60 text-red-300';
    badgeText = 'Flux failed';
  }

  return (
    <div
      className={`rounded-md border overflow-hidden ${
        isSelected ? 'border-emerald-500' : 'border-border'
      }`}
    >
      {variant.previewUrl ? (
        <img
          src={variant.previewUrl}
          alt={`Variant ${variant.variantIndex}`}
          className="aspect-video w-full object-cover"
        />
      ) : (
        <div className="flex aspect-video items-center justify-center text-xs text-muted bg-border/30">
          {variant.bgError ? 'failed' : 'composing…'}
        </div>
      )}
      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between text-xs uppercase tracking-wider">
          <span className="font-medium">
            #{variant.variantIndex} {variant.styleLabel}
          </span>
          {isSelected && <span className="text-emerald-400">✓ picked</span>}
        </div>
        <div className={`rounded px-2 py-1 text-xs ${badgeClass}`}>{badgeText}</div>
        {variant.similarToWorkingTitle && (
          <div className="text-xs text-muted">closest: {variant.similarToWorkingTitle}</div>
        )}
        <button
          onClick={onSelect}
          disabled={disabled || !variant.previewUrl}
          className="btn-primary w-full py-3 text-sm"
        >
          {isSelected ? 'Selected' : 'Use this'}
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// YouTube upload — form + privacy + submit + status polling
// ----------------------------------------------------------------------

function YouTubeUploadPanel({
  production,
  onChanged,
}: {
  production: ProductionDetailResponse['production'];
  onChanged: () => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState(production.publicDescription ?? '');
  const [tags, setTags] = useState('');
  const [privacy, setPrivacy] = useState<'private' | 'unlisted' | 'public'>('unlisted');
  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'queued' | 'processing' | 'done' | 'failed'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function generateMetadata() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.get<{
        metadata: { title: string; description: string; tags: string[]; privacy: 'private' | 'unlisted' | 'public' };
      }>(`/productions/${production.id}/youtube-metadata`);
      setTitle(r.metadata.title);
      setDescription(r.metadata.description);
      setTags(r.metadata.tags.join(', '));
      setPrivacy(r.metadata.privacy);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generate failed');
    } finally {
      setBusy(false);
    }
  }

  async function upload() {
    setError(null);
    setStatus('queued');
    setUploadedUrl(null);
    try {
      const r = await api.post<{ jobId: string }>(
        `/productions/${production.id}/youtube-upload`,
        {
          title,
          description,
          tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
          category: 27,
          privacy,
          language: 'en-GB',
        },
      );
      setJobId(r.jobId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload enqueue failed');
      setStatus('failed');
    }
  }

  useEffect(() => {
    if (!jobId || status === 'done' || status === 'failed') return;
    let cancelled = false;
    const tick = async () => {
      try {
        const rows = await api.get<Array<{ id: string; state: string; outputJson?: { youtubeUrl?: string } | null; errorMessage?: string | null }>>(
          `/productions/${production.id}/postprocess/jobs`,
        );
        const job = rows.find((r) => r.id === jobId);
        if (!job || cancelled) return;
        if (job.state === 'processing') setStatus('processing');
        if (job.state === 'done') {
          setStatus('done');
          setUploadedUrl(job.outputJson?.youtubeUrl ?? null);
          void onChanged();
        }
        if (job.state === 'failed') {
          setStatus('failed');
          setError(job.errorMessage ?? 'Upload failed');
        }
      } catch { /* transient */ }
    };
    void tick();
    const interval = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [jobId, status, production.id, onChanged]);

  return (
    <div className="card space-y-3">
      <div className="text-xs text-muted uppercase tracking-wider">Upload to YouTube</div>
      <button
        onClick={generateMetadata}
        disabled={busy}
        className="btn-outline w-full py-2 text-sm"
      >
        {busy ? '…' : 'Generate metadata from production'}
      </button>

      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title (≤100 chars)"
        className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (≤5000 chars)"
        rows={6}
        className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
      />
      <input
        value={tags}
        onChange={(e) => setTags(e.target.value)}
        placeholder="Tags, comma-separated"
        className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
      />
      <select
        value={privacy}
        onChange={(e) => setPrivacy(e.target.value as 'private' | 'unlisted' | 'public')}
        className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
      >
        <option value="unlisted">Unlisted (recommended for first ship)</option>
        <option value="private">Private</option>
        <option value="public">Public</option>
      </select>

      {error && <div className="text-sm text-danger">{error}</div>}
      {status === 'queued' && <div className="text-xs text-amber-300">⏳ Upload queued; polling.</div>}
      {status === 'processing' && (
        <div className="text-xs text-amber-300">⏳ Hetzner uploading to YouTube (3-10 min for full episodes)…</div>
      )}
      {status === 'done' && uploadedUrl && (
        <div className="text-xs text-emerald-300">
          ✓ Uploaded:{' '}
          <a href={uploadedUrl} target="_blank" rel="noreferrer" className="underline">
            {uploadedUrl}
          </a>
        </div>
      )}

      <button
        onClick={upload}
        disabled={
          status === 'queued' ||
          status === 'processing' ||
          !title.trim() ||
          !description.trim()
        }
        className="btn-primary w-full py-4 text-base"
      >
        {status === 'queued' || status === 'processing' ? 'Uploading…' : 'Upload to YouTube'}
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------
// YouTube update push — for already-uploaded productions
// ----------------------------------------------------------------------

function YouTubeUpdatePanel({
  production,
  onChanged,
}: {
  production: ProductionDetailResponse['production'];
  onChanged: () => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState(production.publicDescription ?? '');
  const [tags, setTags] = useState('');
  const [pushThumbnail, setPushThumbnail] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'queued' | 'processing' | 'done' | 'failed'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function loadCurrent() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.get<{
        metadata: { title: string; description: string; tags: string[] };
      }>(`/productions/${production.id}/youtube-metadata`);
      setTitle(r.metadata.title);
      setDescription(r.metadata.description);
      setTags(r.metadata.tags.join(', '));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Load failed');
    } finally {
      setBusy(false);
    }
  }

  async function pushUpdate() {
    setError(null);
    setStatus('queued');
    try {
      const r = await api.post<{ jobId: string }>(
        `/productions/${production.id}/youtube-update`,
        {
          title: title.trim() || undefined,
          description: description.trim() || undefined,
          tags: tags ? tags.split(',').map((t) => t.trim()).filter(Boolean) : undefined,
          pushThumbnail,
        },
      );
      setJobId(r.jobId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Push failed');
      setStatus('failed');
    }
  }

  useEffect(() => {
    if (!jobId || status === 'done' || status === 'failed') return;
    let cancelled = false;
    const tick = async () => {
      try {
        const rows = await api.get<Array<{ id: string; state: string; errorMessage?: string | null }>>(
          `/productions/${production.id}/postprocess/jobs`,
        );
        const job = rows.find((r) => r.id === jobId);
        if (!job || cancelled) return;
        if (job.state === 'processing') setStatus('processing');
        if (job.state === 'done') {
          setStatus('done');
          void onChanged();
        }
        if (job.state === 'failed') {
          setStatus('failed');
          setError(job.errorMessage ?? 'Push failed');
        }
      } catch { /* transient */ }
    };
    void tick();
    const interval = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [jobId, status, production.id, onChanged]);

  return (
    <div className="card space-y-3">
      <div className="text-xs text-muted uppercase tracking-wider">Push update to YouTube</div>
      <p className="text-xs text-muted">
        Update title / description / tags / thumbnail on the already-uploaded video. Use this to retroactively apply SEO improvements.
      </p>
      <a
        href={`https://www.youtube.com/watch?v=${production.youtubeVideoId}`}
        target="_blank"
        rel="noreferrer"
        className="text-xs text-emerald-400 underline break-all"
      >
        youtube.com/watch?v={production.youtubeVideoId}
      </a>

      <button
        onClick={loadCurrent}
        disabled={busy}
        className="btn-outline w-full py-2 text-sm"
      >
        {busy ? '…' : 'Load current metadata'}
      </button>

      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="New title (leave blank to keep)"
        className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="New description (leave blank to keep)"
        rows={6}
        className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
      />
      <input
        value={tags}
        onChange={(e) => setTags(e.target.value)}
        placeholder="New tags, comma-separated (leave blank to keep)"
        className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
      />
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={pushThumbnail}
          onChange={(e) => setPushThumbnail(e.target.checked)}
          className="h-4 w-4"
        />
        Also push current thumbnail
      </label>

      {error && <div className="text-sm text-danger">{error}</div>}
      {status === 'queued' && <div className="text-xs text-amber-300">⏳ Queued.</div>}
      {status === 'processing' && (
        <div className="text-xs text-amber-300">⏳ Hetzner calling YT Data API…</div>
      )}
      {status === 'done' && (
        <div className="text-xs text-emerald-300">✓ YouTube updated.</div>
      )}

      <button
        onClick={pushUpdate}
        disabled={
          status === 'queued' ||
          status === 'processing' ||
          (!title.trim() && !description.trim() && !tags.trim() && !pushThumbnail)
        }
        className="btn-primary w-full py-4 text-base"
      >
        {status === 'queued' || status === 'processing' ? 'Pushing…' : 'Push update'}
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------
// Mobile source search + import — the entry-point gap. Search the open
// research databases (Archive.org, Wikipedia, Wikisource, Chronicling
// America, HathiTrust, OpenAlex, arXiv, Crossref, Semantic Scholar, CORE,
// Open Library, Google Books) and one-tap import. First import auto-
// advances created → sources_uploaded, unblocking "Draft factsheet".
// ----------------------------------------------------------------------

interface SearchCandidate {
  id: string;
  title: string;
  author?: string;
  year?: number;
  url: string;
  excerpt?: string;
  copyrightHint?: 'public_domain' | 'in_copyright' | 'unknown';
  provider: string;
}

function MobileSourceSearch({
  productionId,
  defaultQuery,
  onImported,
}: {
  productionId: string;
  defaultQuery: string;
  onImported: () => void | Promise<void>;
}) {
  const [query, setQuery] = useState(defaultQuery);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<SearchCandidate[] | null>(null);
  const [importingKey, setImportingKey] = useState<string | null>(null);
  const [importedKeys, setImportedKeys] = useState<Set<string>>(new Set());

  async function search() {
    if (!query.trim()) return;
    setBusy(true);
    setErr(null);
    setCandidates(null);
    try {
      // No providers param = the worker's default fan-out across all
      // adapters. Mobile keeps it simple — no per-provider toggles.
      const r = await api.get<{ candidates: SearchCandidate[] }>(
        `/sources/search?q=${encodeURIComponent(query.trim())}`,
      );
      setCandidates(r.candidates ?? []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function importOne(c: SearchCandidate) {
    const key = `${c.provider}:${c.id}`;
    setImportingKey(key);
    setErr(null);
    try {
      await api.post(`/productions/${productionId}/sources/import`, {
        provider: c.provider,
        id: c.id,
        publicDomain: c.copyrightHint === 'public_domain',
      });
      setImportedKeys((prev) => new Set(prev).add(key));
      // Refresh the parent so the production state (and the "Draft
      // factsheet" button) updates after the first import advances it.
      await onImported();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setImportingKey(null);
    }
  }

  return (
    <div className="card space-y-3">
      <div className="text-xs text-muted uppercase tracking-wider">Add sources</div>
      <p className="text-xs text-muted">
        Search open research databases and tap to import. Your first source unlocks "Draft factsheet".
      </p>
      <div className="flex gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void search(); }}
          placeholder="e.g. Knights Templar"
          className="flex-1 rounded-md border border-border bg-bg px-3 py-3 text-sm"
        />
        <button onClick={search} disabled={busy} className="btn-primary px-4 py-3 text-sm">
          {busy ? '…' : 'Search'}
        </button>
      </div>

      {err && <div className="text-sm text-danger break-words">{err}</div>}

      {candidates && candidates.length === 0 && (
        <p className="text-xs text-muted">No results. Try different words.</p>
      )}

      {candidates && candidates.length > 0 && (
        <ul className="space-y-2">
          {candidates.slice(0, 25).map((c, i) => {
            const key = `${c.provider}:${c.id}`;
            const imported = importedKeys.has(key);
            return (
              <li key={`${key}-${i}`} className="rounded-md border border-border p-3">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium leading-tight line-clamp-2">{c.title}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted">
                      <span className="rounded bg-border/40 px-1.5 py-0.5">{c.provider.replace(/_/g, ' ')}</span>
                      {c.copyrightHint === 'public_domain' && (
                        <span className="rounded bg-emerald-950/60 text-emerald-300 px-1.5 py-0.5">PD</span>
                      )}
                      {c.copyrightHint === 'in_copyright' && (
                        <span className="rounded bg-amber-950/60 text-amber-300 px-1.5 py-0.5">©</span>
                      )}
                      {c.year && <span>{c.year}</span>}
                      {c.author && <span className="normal-case line-clamp-1">{c.author}</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => importOne(c)}
                    disabled={imported || importingKey === key}
                    className={`shrink-0 rounded-md px-3 py-2 text-xs font-medium ${
                      imported ? 'bg-emerald-900/40 text-emerald-300' : 'btn-primary'
                    }`}
                  >
                    {imported ? '✓ Added' : importingKey === key ? '…' : 'Import'}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function BigButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button onClick={onClick} disabled={disabled} className="btn-primary w-full py-5 text-base">
      {children}
    </button>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-accent" />
    </div>
  );
}

function phaseFor(state: string): Phase | 'done' {
  if (state === 'created' || state === 'sources_uploaded') return 'sources';
  if (state.startsWith('factsheet')) return 'factsheet';
  if (state.startsWith('outline')) return 'outline';
  if (state.startsWith('script')) return 'scripts';
  if (
    state.startsWith('preprocess') ||
    state.startsWith('tts') ||
    state.startsWith('audio') ||
    state.startsWith('assembl')
  )
    return 'audio';
  return 'done';
}

function phaseState(current: Phase | 'done', step: Phase): 'done' | 'active' | 'pending' {
  const order: Phase[] = ['sources', 'factsheet', 'outline', 'scripts', 'audio'];
  if (current === 'done') return 'done';
  const ci = order.indexOf(current);
  const si = order.indexOf(step);
  if (si < ci) return 'done';
  if (si === ci) return 'active';
  return 'pending';
}

function formatShortDate(date: string): string {
  const [, month, day] = date.split('-');
  return `${Number(day)}/${Number(month)}`;
}

function formatRelative(epochSeconds: number): string {
  const diff = Math.floor(Date.now() / 1000 - epochSeconds);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
